import { toast } from 'sonner';
import { useGetAllUsers } from '@/hooks';
import { userStore } from '@/store/user';
import { useSendbackProcess } from '@/hooks';
import { useNavigate } from 'react-router-dom';
import { useGetProcessAndForm } from '@/hooks';
import { useTranslation } from 'react-i18next';
import { formStore } from '@/store/form/form-store';
import { useAuthStore } from '@/store/use-auth-store';
import { useCreateProcessedApplication } from '@/hooks';
import FormAnswer from '../form/form-answer/form-answer';
import SearchUser from '@/components/settings/search-user';
import { useFormStore } from '@/store/form-design/form-store';
import React, { type FormEvent, useEffect, useState, useRef } from 'react';
import { useGetAllForms, useGetFormById } from '@/hooks/form/use-form';
import { useFormResponseStore } from '@/store/form/use-form-response-store';
import { useFormDraftStorage } from '@/hooks/form/use-form-draft-storage';
import dayjs from 'dayjs';
import { useGetFormDraft, useSaveFormDraft, useDiscardFormDraft } from '@/hooks/form/use-form-draft';
import { useReturnToApplicant } from '@/hooks/process/use-incoming';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { useGetResponsesByUserIdAndFormId } from '@/hooks/form/use-form-response';
import { useDelegationForForm } from '@/hooks';
import DelegateFormDialog from './delegate-form-dialog';
import DelegationBanner from './delegation-banner';
import type { FormDelegation } from '@/types';

interface PendingFormProps {
    formId: string;
    processId: string;
    applicantProcessId: string;
    completedFormIds: any;
}

// Stored responses are the submit-shaped array; the answer store keys by section
const toStoreShape = (sections: any[]): Record<string, any> => {
    const shaped: Record<string, any> = {};
    (sections ?? []).forEach((section: any) => {
        if (!section?.sectionId) return;
        shaped[section.sectionId] = {
            sectionId: section.sectionId,
            sectionName: section.sectionName,
            questions: Object.fromEntries(
                (section.responses ?? []).map((answer: any) => [answer.questionId, answer])
            ),
        };
    });
    return shaped;
};

const PendingFormComponent: React.FC<PendingFormProps> = ({ formId, processId, applicantProcessId, completedFormIds }) => {
    const { t } = useTranslation();
    const { setFormList } = formStore();
    const { formResponses } = useFormResponseStore();
    const { user } = useAuthStore();
    const { setUsers } = userStore();
    const navigate = useNavigate();

    const { setFormId, setSections, sections } = useFormStore();
    const { formResponses: currentResponses, hydrateResponses } = useFormResponseStore();
    const { data: serverDraft } = useGetFormDraft(applicantProcessId, formId);
    const saveDraftMutation = useSaveFormDraft();
    const discardDraftMutation = useDiscardFormDraft();
    const [draftDismissed, setDraftDismissed] = useState(false);
    const heldDraft = serverDraft?.draft ?? null;
    const heldByOther = !!heldDraft && heldDraft.savedById !== user?.id;

    const toTransformed = () =>
        Object.values(currentResponses).map((section: any) => ({
            sectionId: section.sectionId,
            sectionName: section.sectionName,
            responses: Object.values(section.questions),
        }));

    const handleSaveDraft = async () => {
        if (!user?.id) {
            toast.error(t('processManagement.youMustBeLoggedIn'));
            return;
        }
        try {
            await saveDraftMutation.mutateAsync({
                processId,
                formId,
                applicantProcessId,
                responses: toTransformed(),
            });
            toast.success(t('processManagement.draftSaved'));
        } catch {
            toast.error(t('processManagement.draftSaveFailed'));
        }
    };

    const handleLoadDraft = () => {
        if (!heldDraft?.responses) return;
        hydrateResponses(toStoreShape(heldDraft.responses as any[]));
        setDraftDismissed(true);
        toast.success(t('processManagement.draftLoaded'));
    };

    // A returned application keeps its answers, so reopening the form pre-fills
    // them and the applicant edits rather than starting over.
    const { data: submitted } = useGetResponsesByUserIdAndFormId(
        { userId: user?.id ?? '', formId, applicantProcessId },
        { enabled: !!user?.id && !!formId && !!applicantProcessId }
    );
    const prefilled = useRef(false);

    useEffect(() => {
        if (prefilled.current) return;
        if (Object.keys(currentResponses).length > 0) return; // a draft already won
        const existing = submitted?.responses?.find((r: any) => r.formId === formId);
        if (!existing?.responses) return;
        prefilled.current = true;
        hydrateResponses(toStoreShape(existing.responses as any[]));
    }, [submitted, currentResponses, formId, hydrateResponses]);
    const [returnOpen, setReturnOpen] = useState(false);
    const [returnComment, setReturnComment] = useState('');
    const returnToApplicantMutation = useReturnToApplicant();

    const [delegateOpen, setDelegateOpen] = useState(false);
    const { data: delegationData } = useDelegationForForm(applicantProcessId, formId);
    const delegation: FormDelegation | null = delegationData?.delegation ?? null;

    // Pull what a delegate sent back into the answer store so the owner reviews
    // and can edit it, exactly as the draft banner does.
    const loadDelegationAnswers = (d: FormDelegation) => {
        hydrateResponses(toStoreShape((d.submittedResponses as any[]) ?? []));
    };

    const handleReturnToApplicant = async () => {
        if (!returnComment.trim()) {
            toast.error(t('processManagement.returnReasonRequired'));
            return;
        }
        try {
            await returnToApplicantMutation.mutateAsync({
                applicantProcessId,
                comment: returnComment.trim(),
            });
            toast.success(t('processManagement.returnedToApplicant'));
            setReturnOpen(false);
            setReturnComment('');
            navigate('/staff/incoming/pending-applications');
        } catch {
            toast.error(t('processManagement.returnToApplicantFailed'));
        }
    };
    const { clearDraft } = useFormDraftStorage({
        userId: user?.id,
        processId,
        formId,
        applicantProcessId,
    });
    const [currentSectionId, setCurrentSectionId] = useState<string | null>(null);
    const [selectedNextStaffId, setSelectedNextStaffId] = useState<string>();
    const [navigationHandlers, setNavigationHandlers] = useState<{
        handlePreviousSection: () => void;
        handleNextSection: () => void;
        canGoBack: boolean;
        canGoNext: boolean;
    } | null>(null);

    const createProcessedApplicationMutation = useCreateProcessedApplication();
    const sendbackProcessMutation = useSendbackProcess();

    const { data: formNameData, isLoading: formNameLoading } = useGetAllForms();
    const { data: formDesignData, isLoading: formDesignLoading } = useGetFormById(formId);
    const { data: processAndForm, isLoading: processAndFormLoading } = useGetProcessAndForm(processId, formId);

    // Find the specific process form by formId
    const processForm = processAndForm?.data?.forms?.find((pf: any) => pf.formId === formId);

    // All hooks must be called before any conditional returns
    const staticUsersQuery = useGetAllUsers({ names: ["Staff"] });
    const dynamicUsersQuery = useGetAllUsers({ roleIds: processForm?.nextStepRoles });

    useEffect(() => {
        if (formNameData) {
            setFormList(formNameData);
        }
    }, [formNameData, setFormList]);

    useEffect(() => {
        if (formId && formNameData && formDesignData) {
            // Handle both old structure (sections) and new structure (design)
            const sections = formDesignData.sections || formDesignData.design || [];
            setSections(sections);
            setFormId(formId);
        }
    }, [formId, formNameData, formDesignData, setSections, setFormId]);

    useEffect(() => {
        if (processForm?.nextStepType === "STATIC") {
            setUsers(staticUsersQuery.data);
        } else if (processForm?.nextStepType === "DYNAMIC" && processForm?.nextStepSpecifiedTo === "SINGLE_STAFF") {
            setUsers(dynamicUsersQuery.data);
        }
    }, [staticUsersQuery.data, dynamicUsersQuery.data, processForm]);


    // Show loading state if any critical data is still loading
    if (processAndFormLoading || formDesignLoading || formNameLoading) {
        return (
            <div className="flex items-center justify-center p-8">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
                    <p className="text-gray-600">{t('processManagement.loading')}</p>
                </div>
            </div>
        );
    }

    // Show error state if processForm is not found
    if (!processForm) {
        return (
            <div className="flex items-center justify-center p-8">
                <div className="text-center">
                    <p className="text-red-600">{t('processManagement.formNotFound')}</p>
                    <p className="text-sm text-gray-500 mt-2">Form ID: {formId}</p>
                </div>
            </div>
        );
    }

    const handleSubmit = async (event: FormEvent<HTMLButtonElement>) => {
        event.preventDefault();

        if (!user?.id) {
            toast.error(t('processManagement.youMustBeLoggedIn'));
            return;
        }

        if ((processForm?.nextStepType === "DYNAMIC" &&
                processForm?.nextStepSpecifiedTo === "SINGLE_STAFF") && !selectedNextStaffId) {
            toast.error(t('processManagement.selectStaffToSubmitTo'));
            return;
        }

        try {
            // Transform formResponses to match bulk upload format
            // Convert from: { sectionId: { questions: { questionId: {...} } } }
            // To: [{ sectionId, sectionName, responses: [...] }]
            const transformedResponses = Object.values(formResponses).map(section => ({
                sectionId: section.sectionId,
                sectionName: section.sectionName,
                responses: Object.values(section.questions)
            }));

            const prResponse = await createProcessedApplicationMutation.mutateAsync({
                applicantProcessId,
                formId,
                reviewerId: user.id,
                nextStaffId: selectedNextStaffId,
                responses: transformedResponses,
            });

            if (prResponse) {
                clearDraft();
                if (heldDraft) {
                    discardDraftMutation.mutate({ applicantProcessId, formId });
                }
                toast.success(t('common.processUpdatedSuccessfully'));
                navigate('/staff/incoming/pending-applications');
            } else {
                toast.error(t('processManagement.submissionFailed'));
            }
        } catch (error) {
            toast.error(t('processManagement.anErrorOccurred'));
            console.error('Submission error:', error);
        }
    };

    const handleSendBackProcess = async () => {
        try {
            const response = await sendbackProcessMutation.mutateAsync({ applicantProcessId, formId });
            if (response.success) {
                toast.success(t('processManagement.sentBackSuccessfully'));
                navigate('/staff/incoming/pending-applications');
            }
        } catch (error) {
            toast.error(t('processManagement.anErrorOccurred'));
            console.error('Send back error:', error);
        }
    };

    return (
        <form className="">
            <div className='flex items-center justify-end gap-3 w-full pr-4'>
                <button
                    type='button'
                    className='px-4 py-2 border border-darkBlue text-darkBlue rounded text-sm'
                    onClick={handleSaveDraft}
                    disabled={saveDraftMutation.isPending}
                >
                    {saveDraftMutation.isPending
                        ? t('processManagement.savingDraft')
                        : t('processManagement.saveDraft')}
                </button>
                {!delegation && (
                    <button
                        type='button'
                        className='px-4 py-2 border border-darkBlue text-darkBlue rounded text-sm'
                        onClick={() => setDelegateOpen(true)}
                    >
                        {t('delegation.owner.delegate')}
                    </button>
                )}
                {completedFormIds.length > 0 && (
                    <button
                        type='button'
                        className='px-4 py-2 border border-amber-500 text-amber-700 rounded text-sm'
                        onClick={() => setReturnOpen(true)}
                    >
                        {t('processManagement.returnToApplicant')}
                    </button>
                )}
                {completedFormIds.length > 1 && (
                    <button type='button' className='main-dark-button' onClick={handleSendBackProcess}>
{sendbackProcessMutation.isPending ? t('processManagement.sending') : t('processManagement.sendBack')}
                    </button>
                )}
            </div>

            {delegation && (
                <DelegationBanner
                    delegation={delegation}
                    applicantProcessId={applicantProcessId}
                    formId={formId}
                    onLoadAnswers={loadDelegationAnswers}
                    nextStaffId={selectedNextStaffId}
                    requiresNextStaff={
                        processForm?.nextStepType === 'DYNAMIC' &&
                        processForm?.nextStepSpecifiedTo === 'SINGLE_STAFF'
                    }
                    onApproved={() =>
                        navigate('/staff/incoming/pending-applications')
                    }
                />
            )}

            {heldDraft && !draftDismissed && (
                <div className='mx-4 mb-4 p-3 rounded border border-amber-300 bg-amber-50 text-sm flex flex-wrap items-center justify-between gap-3'>
                    <span className='text-amber-900'>
                        {heldByOther
                            ? t('processManagement.draftHeldByOther', {
                                  name: `${heldDraft.savedBy?.firstName ?? ''} ${heldDraft.savedBy?.lastName ?? ''}`.trim() || heldDraft.savedBy?.email,
                                  time: dayjs(heldDraft.updatedAt).format('MMM D, YYYY, h:mm A'),
                              })
                            : t('processManagement.draftHeldByYou', {
                                  time: dayjs(heldDraft.updatedAt).format('MMM D, YYYY, h:mm A'),
                              })}
                    </span>
                    <span className='flex items-center gap-2'>
                        <button
                            type='button'
                            className='px-3 py-1 rounded bg-darkBlue text-white text-xs'
                            onClick={handleLoadDraft}
                        >
                            {t('processManagement.loadDraft')}
                        </button>
                        <button
                            type='button'
                            className='px-3 py-1 rounded border border-amber-400 text-amber-900 text-xs'
                            onClick={() => setDraftDismissed(true)}
                        >
                            {t('common.dismiss')}
                        </button>
                    </span>
                </div>
            )}

            <FormAnswer 
                setCurrentFormSectionId={setCurrentSectionId}
                onNavigationHandlersReady={setNavigationHandlers}
            />

            {sections && sections.length > 0 && sections.findIndex((section) => section.id === currentSectionId) === sections.length - 1 && (
                <div className='md:px-4 space-y-4'>
                    {(processForm?.nextStepType === "DYNAMIC" &&
                            processForm?.nextStepSpecifiedTo === "SINGLE_STAFF") && (
                            <div className='max-w-md space-y-1'>
                                <div className='main-label font-medium'>{t('processManagement.selectStaffToSubmitTo')}</div>
                                <SearchUser setUserId={setSelectedNextStaffId} />
                            </div>
                        )}

                    {delegation && (
                        <p className='max-w-xl rounded border border-amber-300 bg-amber-50 p-2 text-xs text-amber-800'>
                            {t('delegation.owner.supersedeWarning')}
                        </p>
                    )}

                    <div className="flex space-x-4 items-center">
                        {navigationHandlers?.canGoBack && (
                            <button
                                type="button"
                                onClick={navigationHandlers.handlePreviousSection}
                                className="px-4 py-2 bg-blue-900 text-white rounded"
                            >
                                {t('common.back')}
                            </button>
                        )}
                        <button
                            type="button"
                            className={`main-dark-button text-sm ${createProcessedApplicationMutation.isPending && '!bg-darkBlue/60'}`}
                            onClick={handleSubmit}
                            disabled={createProcessedApplicationMutation.isPending}
                        >
                            {createProcessedApplicationMutation.isPending ? t('processManagement.submitting') : t('processManagement.submit')}
                        </button>
                    </div>
                </div>
            )}        {returnOpen && (
                <Dialog open={returnOpen} onOpenChange={(open) => !open && setReturnOpen(false)}>
                    <DialogContent className='sm:rounded-md max-w-[560px]'>
                        <DialogHeader>
                            <DialogTitle>{t('processManagement.returnToApplicant')}</DialogTitle>
                            <DialogDescription>
                                {t('processManagement.returnToApplicantDescription')}
                            </DialogDescription>
                        </DialogHeader>
                        <textarea
                            className='main-input w-full min-h-[120px]'
                            placeholder={t('processManagement.returnReasonPlaceholder')}
                            value={returnComment}
                            onChange={(e) => setReturnComment(e.target.value)}
                        />
                        <div className='flex justify-end gap-3 pt-2'>
                            <button
                                type='button'
                                className='px-4 py-2 border rounded text-sm'
                                onClick={() => setReturnOpen(false)}
                            >
                                {t('processManagement.cancel')}
                            </button>
                            <button
                                type='button'
                                className='main-dark-button text-sm'
                                onClick={handleReturnToApplicant}
                                disabled={returnToApplicantMutation.isPending || !returnComment.trim()}
                            >
                                {returnToApplicantMutation.isPending
                                    ? t('processManagement.returning')
                                    : t('processManagement.confirmReturn')}
                            </button>
                        </div>
                    </DialogContent>
                </Dialog>
            )}

            <DelegateFormDialog
                open={delegateOpen}
                onClose={() => setDelegateOpen(false)}
                processId={processId}
                formId={formId}
                applicantProcessId={applicantProcessId}
            />
        </form>
    );
};

export default PendingFormComponent;